import type { FormQuestion, FormResponse } from "../../types/form";
import { ALPHA_COUNTRIES } from "./alphaCountries";

export type AlphaIbkrAccountType = "taxable" | "retirement";
export type AlphaIbkrAccountDetailField = "accountName" | "countryCode" | "accountType";

export interface AlphaIbkrAccountDetails {
  accountName: string;
  countryCode: string;
  accountType: AlphaIbkrAccountType;
}

export interface AlphaIbkrAccountDetailQuestionIds {
  accountName: number;
  countryCode: number;
  accountType: number;
}

export type SavedAlphaIbkrAccountDetails = Partial<AlphaIbkrAccountDetails>;

const QUESTION_NAMES: Record<AlphaIbkrAccountDetailField, string> = {
  accountName: "what's your name as it appears in your interactive brokers account?",
  countryCode: "what country is your account located in?",
  accountType: "type of account?",
};

const COUNTRY_CODES = new Set(ALPHA_COUNTRIES.map((country) => country.code));

export class AlphaIbkrAccountDetailsContractError extends Error {}

const normalize = (value: unknown): string => (
  typeof value === "string" ? value.trim().toLowerCase() : ""
);

const getQuestion = (
  questions: FormQuestion[],
  field: AlphaIbkrAccountDetailField
): FormQuestion => {
  const matches = questions.filter((question) => (
    normalize(question.question) === QUESTION_NAMES[field]
    && normalize(question.category) === "kyc"
    && normalize(question.subcategory) === "kyc"
  ));

  if (matches.length !== 1) {
    throw new AlphaIbkrAccountDetailsContractError(
      `IBKR account details question contract is invalid for ${field}`
    );
  }

  const question = matches[0];
  if (!Number.isInteger(question.id) || question.id <= 0) {
    throw new AlphaIbkrAccountDetailsContractError(
      `IBKR account details question ID is invalid for ${field}`
    );
  }
  if (normalize(question.type) !== "text" || question.options.trim() !== "") {
    throw new AlphaIbkrAccountDetailsContractError(
      `IBKR account details question type is invalid for ${field}`
    );
  }

  return question;
};

export function getAlphaIbkrAccountDetailQuestionIds(
  questions: FormQuestion[]
): AlphaIbkrAccountDetailQuestionIds {
  return {
    accountName: getQuestion(questions, "accountName").id,
    countryCode: getQuestion(questions, "countryCode").id,
    accountType: getQuestion(questions, "accountType").id,
  };
}

const responseForQuestion = (
  responses: FormResponse[],
  questionId: number,
  field: AlphaIbkrAccountDetailField
): FormResponse | null => {
  const matches = responses.filter((response) => response.questionId === questionId);
  if (matches.length > 1) {
    throw new AlphaIbkrAccountDetailsContractError(
      `Saved IBKR account details response contract is invalid for ${field}`
    );
  }
  return matches[0] ?? null;
};

const parseSavedAccountType = (
  response: FormResponse
): AlphaIbkrAccountType => {
  const accountType = response.answer.trim().toLowerCase();
  if (accountType !== "taxable" && accountType !== "retirement") {
    throw new AlphaIbkrAccountDetailsContractError(
      "Saved IBKR account type is invalid"
    );
  }
  return accountType;
};

// Reads only the account-type answer needed to select a managed mix. Account
// name, country, and setup completion remain separate onboarding concerns.
export function getSavedAlphaIbkrAccountType(
  questions: FormQuestion[],
  responses: FormResponse[]
): AlphaIbkrAccountType | null {
  const question = getQuestion(questions, "accountType");
  const response = responseForQuestion(responses, question.id, "accountType");
  return response ? parseSavedAccountType(response) : null;
}

export function getSavedAlphaIbkrAccountDetails(
  questionIds: AlphaIbkrAccountDetailQuestionIds,
  responses: FormResponse[]
): SavedAlphaIbkrAccountDetails {
  const saved: SavedAlphaIbkrAccountDetails = {};
  const nameResponse = responseForQuestion(responses, questionIds.accountName, "accountName");
  const countryResponse = responseForQuestion(responses, questionIds.countryCode, "countryCode");
  const typeResponse = responseForQuestion(responses, questionIds.accountType, "accountType");

  if (nameResponse) {
    const accountName = nameResponse.answer.trim();
    if (!accountName) {
      throw new AlphaIbkrAccountDetailsContractError(
        "Saved IBKR account name is invalid"
      );
    }
    saved.accountName = accountName;
  }

  if (countryResponse) {
    const countryCode = countryResponse.answer.trim().toUpperCase();
    if (!COUNTRY_CODES.has(countryCode)) {
      throw new AlphaIbkrAccountDetailsContractError(
        "Saved IBKR account country is invalid"
      );
    }
    saved.countryCode = countryCode;
  }

  if (typeResponse) {
    saved.accountType = parseSavedAccountType(typeResponse);
  }

  return saved;
}

export const areAlphaIbkrAccountDetailsComplete = (
  details: SavedAlphaIbkrAccountDetails
): details is AlphaIbkrAccountDetails => Boolean(
  details.accountName
  && details.countryCode
  && details.accountType
);
